import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Flowbite } from 'flowbite-react';
import { Button, Label } from 'flowbite-react';
import { serverURL } from '../constants';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { AiOutlineLoading } from 'react-icons/ai';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import { resolveHomePath, getSessionAuth, persistLoginSession, currentBrowserHost } from '../shared/auth/session';
import AuthOrgBrand, { useOrgAuthUi } from '../shared/branding/AuthOrgBrand';
import AuthShell, { AuthCard } from '../shared/auth/AuthShell';

const SignIn = () => {
    const { t } = useTranslation();
    const authUi = useOrgAuthUi();
    const [email, setEmail] = useState('');
    const [processing, setProcessing] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const isRedirecting = useRef(false);

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const redirectSignUp = useCallback(() => {
        const invite = inviteCode.trim() || searchParams.get('invite') || '';
        navigate(invite ? `/signup?invite=${encodeURIComponent(invite)}` : '/signup');
    }, [navigate, inviteCode, searchParams]);

    const redirectForgot = useCallback(() => {
        navigate("/forgot");
    }, [navigate]);

    const redirectHome = useCallback(() => {
        if (location.state && location.state.courseIdea && location.state.redirectToCreate) {
            navigate("/create-choice", { replace: true, state: { courseIdea: location.state.courseIdea } });
        } else if (location.state && location.state.courseIdeaData && location.state.redirectToCourseIdeas) {
            navigate("/course-ideas", { replace: true, state: { prefillAnswers: location.state.courseIdeaData } });
        } else {
            window.location.href = resolveHomePath(getSessionAuth());
        }
    }, [navigate, location.state]);

    const finishLoginRedirect = useCallback(async () => {
        isRedirecting.current = true;
        const path = resolveHomePath(getSessionAuth());
        logger.info('Redirecting after login to', path);
        window.location.href = path;
    }, []);

    useEffect(() => {
        const inviteFromUrl = searchParams.get('invite');
        if (inviteFromUrl) setInviteCode(inviteFromUrl.trim().toUpperCase());
    }, [searchParams]);

    useEffect(() => {
        const checkAuthAndRedirect = async () => {
            const session = getSessionAuth();
            if (session.auth && !isRedirecting.current) {
                if (session.email) {
                    await finishLoginRedirect(session.email);
                } else {
                    isRedirecting.current = true;
                    redirectHome();
                }
            }
        };

        if (window.location.pathname === '/signin' && !isRedirecting.current) {
            checkAuthAndRedirect();
        }
    }, [finishLoginRedirect, redirectHome]);

    const showToast = async (msg, type = 'error') => {
        setProcessing(false);
        toast(msg, {
            type: type,
            position: "top-center",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            style: {
                backgroundColor: type === 'error' ? '#ef4444' : '#10b981',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500'
            }
        });
    }

    const clearErrors = () => {
        setError('');
        setEmailError('');
        setPasswordError('');
    }

    const handleSignin = async (e) => {
        e.preventDefault();
        clearErrors();
        
        logger.info('Sign in button clicked');
        
        // Validation
        let hasError = false;
        if (!email) {
            setEmailError(t('signin.validation.emailRequired', 'Email is required'));
            hasError = true;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError(t('signin.validation.emailInvalid', 'Please enter a valid email address'));
            hasError = true;
        }
        
        if (!password) {
            setPasswordError(t('signin.validation.passwordRequired', 'Password is required'));
            hasError = true;
        }
        
        if (hasError) {
            setError(t('signin.validation.requiredFields', 'Please fill in all required fields'));
            showToast(t('signin.validation.requiredFields', 'Please fill in all required fields'), 'error');
            return;
        }
        
        logger.info('Attempting to sign in with:', { email });
        
        const postURL = serverURL + '/api/signin';
        try {
            setProcessing(true);
            logger.info('Sending request to:', postURL);
            
            const response = await axios.post(postURL, {
                email,
                password,
                inviteCode: inviteCode.trim() || undefined,
                host: currentBrowserHost(),
            }, {
                timeout: 30000, // Increased to 30 seconds for better reliability
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            logger.info('Response received:', response.data);
            
            if (response.data.success) {
                logger.info('Sign in successful!');
                clearErrors();
                showToast(response.data.message || t('signin.success', 'Sign in successful!'), 'success');
                if (!response.data.accessToken && !response.data.token) {
                    const msg = t('signin.errors.missingToken', 'Sign-in did not return a secure session. Please try again or contact support.');
                    setError(msg);
                    showToast(msg, 'error');
                    return;
                }
                persistLoginSession(response.data.userData, response.data.uploadLimits, {
                    accessToken: response.data.accessToken || response.data.token,
                    refreshToken: response.data.refreshToken,
                    organizationId: response.data.organizationId,
                    orgRole: response.data.orgRole,
                    orgSlug: response.data.orgSlug || authUi.slug,
                    platformAdmin: response.data.platformAdmin,
                });
                if (response.data.inviteRedemption?.success) {
                    showToast(t('signup.invite.redeemed', 'Class invite applied successfully!'), 'success');
                }
                
                if (sessionStorage.getItem('shared') === null) {
                    isRedirecting.current = false;
                    await finishLoginRedirect(response.data.userData.email);
                    return;
                }

                logger.info('Processing shared data');
                getDataFromDatabase(sessionStorage.getItem('shared'));
            } else {
                logger.info('Sign in failed:', response.data.message);
                const errorMsg = response.data.message || t('signin.errors.invalidCredentials', 'Invalid email or password');
                setError(errorMsg);
                setPasswordError(errorMsg);
                showToast(errorMsg, 'error');
            }
        } catch (error) {
            logger.error('Signin error:', error);
            let errorMsg = '';
            if (error.response?.data?.code === 'SSO_REQUIRED') {
                errorMsg = error.response.data.message;
                const slug = error.response.data.slug;
                if (slug) {
                    window.location.href = `${serverURL}/api/auth/sso/start?slug=${encodeURIComponent(slug)}`;
                    return;
                }
            } else if (error.response?.data?.code === 'ORG_ACCESS_REQUIRED') {
                errorMsg = error.response.data.message;
            } else if (error.code === 'ECONNABORTED') {
                errorMsg = t('signin.errors.timeout', 'Request timed out. The server may be slow or unavailable. Please check your connection and try again.');
            } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                errorMsg = t('signin.errors.networkError', 'Network error. Please check your internet connection and ensure the server is running.');
            } else if (error.response?.status === 503) {
                errorMsg = t('signin.errors.serviceUnavailable', 'Service temporarily unavailable. Please try again later.');
            } else if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMsg = t('signin.errors.invalidCredentials', 'Invalid email or password');
            } else if (!error.response) {
                errorMsg = t('signin.errors.serverUnreachable', 'Unable to reach the server. Please ensure the backend server is running on ' + serverURL);
            } else {
                errorMsg = error.message || t('signin.errors.serverError', 'An error occurred. Please try again.');
            }
            setError(errorMsg);
            setPasswordError(errorMsg);
            showToast(errorMsg, 'error');
        } finally {
            setProcessing(false);
        }
    };


    async function getDataFromDatabase(id) {
        const postURL = serverURL + `/api/shareable?id=${id}`;
        try {
            const response = await axios.get(postURL, {
                timeout: 10000, // 10 second timeout for production
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const dat = response.data[0].content;
            const jsonData = JSON.parse(dat);
            const type = response.data[0].type.toLowerCase();
            const mainTopic = response.data[0].mainTopic;
            const user = sessionStorage.getItem('uid');
            const content = JSON.stringify(jsonData);

            const postURLs = serverURL + '/api/courseshared';
            const responses = await axios.post(postURLs, { user, content, type, mainTopic }, {
                timeout: 10000, // 10 second timeout for production
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (responses.data.success) {
                sessionStorage.removeItem('shared');
                isRedirecting.current = false;
                const email = sessionStorage.getItem('email');
                if (email) await finishLoginRedirect(email);
                else redirectHome();
            } else {
                isRedirecting.current = false;
                const email = sessionStorage.getItem('email');
                if (email) await finishLoginRedirect(email);
                else redirectHome();
            }
        } catch (error) {
            logger.error('getDataFromDatabase error:', error);
            isRedirecting.current = false;
            const email = sessionStorage.getItem('email');
            if (email) await finishLoginRedirect(email);
            else redirectHome();
        }
    }

    return (
        <Flowbite>
            <AuthShell quoteKey="signin" showMarketing={!authUi.isTenantHost}>
                <AuthCard>
                    <AuthOrgBrand className="mb-5" />
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                            {authUi.isTenantHost
                                ? t('signin.titleOrg', 'Sign in to {{name}}', { name: authUi.companyName })
                                : t('signin.title', 'Sign in')}
                        </h1>
                        <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
                            {authUi.isTenantHost
                                ? t('signin.subtitleOrg', 'Use your organization account')
                                : t('signin.subtitle', 'Welcome back to Coursolo')}
                        </p>
                    </div>

                    {authUi.ssoEnabled && (
                        <div className="mb-5">
                            <a
                                href={`${serverURL}/api/auth/sso/start?${authUi.slug ? `slug=${encodeURIComponent(authUi.slug)}` : `organizationId=${encodeURIComponent(authUi.organizationId || '')}`}`}
                                className="w-full inline-flex items-center justify-center py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-95"
                                style={{ backgroundColor: authUi.primary }}
                            >
                                {t('signin.sso', 'Continue with SSO')}
                            </a>
                            {!authUi.ssoEnforce && (
                                <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                                    or
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                                </div>
                            )}
                        </div>
                    )}

                    {error ? (
                        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300">
                            {error}
                        </div>
                    ) : null}

                    {!authUi.ssoEnforce && (
                        <form onSubmit={handleSignin} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="email1">
                                    {t('signin.email', 'Email')}
                                </Label>
                                <input
                                    id="email1"
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (emailError) setEmailError('');
                                        if (error) clearErrors();
                                    }}
                                    value={email}
                                    className={`w-full rounded-xl border bg-white dark:bg-slate-950 px-3.5 py-2.5 text-sm ${
                                        emailError ? 'border-red-400' : 'border-slate-200 dark:border-slate-600'
                                    }`}
                                    type="email"
                                    placeholder={t('signin.emailPlaceholder', 'you@example.com')}
                                    autoComplete="email"
                                />
                                {emailError ? <p className="text-xs text-red-600">{emailError}</p> : null}
                            </div>

                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="password1">
                                        {t('signin.password', 'Password')}
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={redirectForgot}
                                        className="text-xs font-medium hover:underline"
                                        style={{ color: authUi.primary }}
                                    >
                                        {t('signin.forgotPassword', 'Forgot password?')}
                                    </button>
                                </div>
                                <div className="relative">
                                    <input
                                        id="password1"
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (passwordError) setPasswordError('');
                                            if (error) clearErrors();
                                        }}
                                        value={password}
                                        className={`w-full rounded-xl border bg-white dark:bg-slate-950 px-3.5 py-2.5 pr-11 text-sm ${
                                            passwordError ? 'border-red-400' : 'border-slate-200 dark:border-slate-600'
                                        }`}
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('signin.passwordPlaceholder', 'Your password')}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                                        aria-label={showPassword ? t('common.hidePassword', 'Hide password') : t('common.showPassword', 'Show password')}
                                    >
                                        {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {passwordError ? <p className="text-xs text-red-600">{passwordError}</p> : null}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="signinInvite">
                                    {t('signup.invite.labelShort', 'Class code (optional)')}
                                </Label>
                                <input
                                    id="signinInvite"
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                    className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3.5 py-2.5 text-sm uppercase tracking-wider"
                                    placeholder={t('signup.invite.placeholder', 'TCH-XXXXXX')}
                                />
                            </div>

                            <Button
                                isProcessing={processing}
                                processingSpinner={<AiOutlineLoading className="h-5 w-5 animate-spin" />}
                                className="w-full !rounded-xl py-2.5 text-sm font-semibold text-white enabled:hover:opacity-95"
                                style={{ backgroundColor: authUi.primary }}
                                type="submit"
                            >
                                {t('signin.signIn', 'Sign in')}
                            </Button>
                        </form>
                    )}

                    <div className="mt-6 pt-5 border-t border-slate-100 dark:border-slate-800 space-y-3 text-center">
                        {(authUi.allowSelfSignup || inviteCode.trim()) && !authUi.ssoEnforce ? (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t('signin.noAccount', "Don't have an account?")}{' '}
                                <button
                                    type="button"
                                    onClick={redirectSignUp}
                                    className="font-semibold hover:underline"
                                    style={{ color: authUi.primary }}
                                >
                                    {t('signin.createAccount', 'Create account')}
                                </button>
                            </p>
                        ) : !authUi.allowSelfSignup ? (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t('signin.inviteOnly', 'This organization requires an invite to create an account.')}{' '}
                                <button type="button" onClick={redirectSignUp} className="font-semibold hover:underline" style={{ color: authUi.primary }}>
                                    {t('signin.haveInvite', 'I have an invite')}
                                </button>
                            </p>
                        ) : null}

                        {!authUi.isTenantHost ? (
                            <p className="text-xs text-slate-400">
                                <Link to="/organizations/start" className="font-medium hover:underline" style={{ color: authUi.primary }}>
                                    {t('signin.createSchoolWorkspace', 'Create a school or company workspace')}
                                </Link>
                            </p>
                        ) : null}
                    </div>
                </AuthCard>
            </AuthShell>
        </Flowbite>
    );
};

export default SignIn;